/**
 * Envelope canônico de evento (§9.1, task B21).
 *
 * ── Uma forma para tudo que entra ─────────────────────────────────────────
 *
 * Chatwoot, Twenty e os treze portais mandam formatos diferentes. O envelope é
 * o que faz o resto do sistema — worker, saga, reconciliação, replay — não
 * precisar saber de qual deles o evento veio. O adapter traduz uma vez, na
 * borda; daí para dentro é isto.
 *
 * ── Validado nas duas pontas ──────────────────────────────────────────────
 *
 * Na entrada do ingress e **na saída da fila**. Parece redundante e não é: entre
 * as duas há uma fila, e o que sai dela pode ter sido publicado por uma versão
 * mais antiga do produtor, ou reprocessado de um payload guardado. Validar só na
 * entrada confia que nada mudou no meio.
 */

import { z } from 'zod';
import { IdSchema } from '../v1/comum.js';
import { InstanteSchema } from '../v1/valores.js';

/**
 * Origem do evento. Estável — vira parte da chave de deduplicação
 * `(tenant_id, source, event_type, source_event_id)`, e renomear uma origem
 * faria todo o histórico dela deixar de deduplicar.
 */
export const OrigemSchema = z.enum([
  'chatwoot',
  'twenty',
  'olx_chat',
  'olx_lead',
  'webmotors',
  'usadosbr',
  'icarros',
  'gmn',
  'rdstation',
  'autoforce',
  'mercadolivre',
  'mobiauto',
  'socarrao',
  'autocarro',
  'napista',
  /** Evento produzido por nós — automação, reconciliação, replay. */
  'cliqcrm',
]);

export type Origem = z.infer<typeof OrigemSchema>;

/**
 * Sobre o que o evento é (§9.1).
 *
 * ── A armadilha da B21 ────────────────────────────────────────────────────
 *
 * *"Deixar `subject` para depois. É por ele que se busca 'tudo que aconteceu
 * com esta conversa', e retroalimentar o campo em eventos antigos não é
 * possível."*
 *
 * O `id` é nulo até a escada de identidade da §5.2 resolver quem é — um lead
 * que chega sem telefone conhecido não tem cliente canônico ainda. Nulo é
 * estado legítimo; ausente do schema não seria.
 */
export const AssuntoSchema = z.object({
  type: z.enum([
    'conversation',
    'message',
    'customer',
    'company',
    'opportunity',
    'task',
    'vehicle_interest',
    'connection',
  ]),
  id: IdSchema.nullable().describe('Canônico. Nulo até a identidade resolver (§5.2).'),
});

/**
 * Catálogo de tipos de evento.
 *
 * Hierárquico e **estável**: renomear um tipo quebra consumidor e histórico ao
 * mesmo tempo — o consumidor para de reconhecer, e os eventos já gravados
 * passam a ter um tipo que não existe mais. Trata-se como contrato.
 *
 * Acrescentar é livre. `envelope.test.mts` congela a lista, pelo mesmo motivo
 * que o catálogo de erros da B34 é congelado.
 */
export const TIPOS_DE_EVENTO = [
  // Conversa (§8)
  'conversation.created',
  'conversation.updated',
  'conversation.status_changed',
  'conversation.assigned',
  'conversation.message.created',
  'conversation.message.status_changed',
  /*
   * O cliente escolheu uma opção do menu (#1012). O motor de conversa entrega
   * só uma mensagem recebida; o CRM a reconhece como escolha ao projetá-la e
   * publica este tipo para o serviço de automações, além da mensagem.
   */
  'conversation.button_reply',
  // Cliente (§5)
  'customer.created',
  'customer.updated',
  // D01: o Twenty distingue `deleted` (soft) de `destroyed` (hard); os dois
  // chegam aqui como remoção, porque a diferença é do lado dele — a nossa
  // projeção MARCA nos dois casos (Armadilha da D01).
  'customer.deleted',
  'customer.merged',
  'customer.identity_linked',
  // Empresa (§7.1) — projetada pela D01, sem tela própria ainda.
  'company.created',
  'company.updated',
  'company.deleted',
  // Comercial (§10)
  'opportunity.created',
  'opportunity.updated',
  'opportunity.deleted',
  'opportunity.stage_changed',
  'opportunity.won',
  'opportunity.lost',
  'task.created',
  'task.updated',
  'task.deleted',
  'task.completed',
  // O objeto não existe no workspace do Twenty ainda (A01 §4, criá-lo é a
  // §7.2). O tipo entra com a projeção que o espera (D01, sinal da C11).
  'vehicle_interest.created',
  'vehicle_interest.updated',
  'vehicle_interest.deleted',
  // Integração (§12)
  'connection.health_changed',
  'connection.credential_expired',
  // Nossos
  'lead.received',
  /*
   * O terceiro evento canônico da §12.1 (task E02t): algo mudou NO PORTAL sem
   * ser lead nem conversa — o anúncio que saiu do ar, a nota da revenda que
   * caiu, a campanha que terminou.
   *
   * Um tipo só para os quatro casos porque a pergunta é a mesma; o que muda é o
   * `subject`, e é ele que diz se foi anúncio ou reputação. Quatro tipos
   * criariam quatro filas para o mesmo trabalho.
   */
  'source_entity.updated',
  'automation.executed',
] as const;

export const TipoDeEventoSchema = z.enum(TIPOS_DE_EVENTO);
export type TipoDeEvento = z.infer<typeof TipoDeEventoSchema>;

/**
 * Versão do formato do envelope.
 *
 * Regra de evolução: acrescentar campo **opcional** não muda a versão; remover
 * campo, torná-lo obrigatório ou mudar o significado de um existente, muda. O
 * consumidor lê a versão e decide — sem ela, teria de adivinhar pelo formato.
 */
export const VERSAO_DO_ENVELOPE = 1;

export const EnvelopeSchema = z
  .object({
    event_id: IdSchema,
    tenant_id: IdSchema,

    source: OrigemSchema,
    source_account: z.string().min(1).describe('Conta da organização no provedor'),
    /**
     * Identificador do evento **na origem**. Compõe a chave de deduplicação.
     *
     * Alguns provedores não têm um: a §9.3 resolve compondo — `message:987:` mais
     * o `updated_at` — para que a mesma mensagem editada duas vezes conte como
     * dois eventos, e a mesma entrega repetida conte como um.
     */
    source_event_id: z.string().min(1),

    event_type: TipoDeEventoSchema,
    schema_version: z.number().int().min(1),

    occurred_at: InstanteSchema.describe('Quando aconteceu NA ORIGEM'),
    received_at: InstanteSchema.describe('Quando chegou aqui'),

    correlation_id: IdSchema,
    /**
     * Evento que causou este. Sem uso imediato, e presente desde o começo de
     * propósito: é o que permite reconstruir a cadeia quando um incidente for
     * investigado, e acrescentá-lo depois deixaria um buraco no histórico
     * exatamente na parte que ninguém consegue reconstituir.
     */
    causation_id: IdSchema.nullable(),

    subject: AssuntoSchema,

    /** Bruto, como veio. Ausente depois da purga de 90 dias (ADR-0009). */
    payload: z.unknown().optional(),

    trace: z.object({
      attempt: z.number().int().min(0),
    }),
  })
  .describe('Envelope canônico da §9.1');

export type Envelope = z.infer<typeof EnvelopeSchema>;

/**
 * Valida um envelope, com mensagem que diz onde está o problema.
 *
 * Existe separado do `parse` cru porque o erro chega em dois lugares muito
 * diferentes — a borda do ingress, com um webhook de terceiro na mão, e a saída
 * da fila, com uma mensagem que já foi aceita antes. Nos dois casos, "o que
 * exatamente está errado" é a primeira pergunta.
 */
export function validarEnvelope(valor: unknown): Envelope {
  const resultado = EnvelopeSchema.safeParse(valor);
  if (resultado.success) return resultado.data;

  const problemas = resultado.error.issues
    .map((issue) => `${issue.path.join('.') || '(raiz)'}: ${issue.message}`)
    .join('; ');
  throw new EnvelopeInvalidoError(problemas);
}

export class EnvelopeInvalidoError extends Error {
  constructor(readonly problemas: string) {
    super(`Envelope fora do contrato da §9.1 — ${problemas}`);
    this.name = 'EnvelopeInvalidoError';
  }
}
